import type { UsePrompt } from '@studio/design-system/prompt';
import { clear } from '#~/notifications/index.js';

import { locateProxy } from '#~/agent/index.js';
import type { Device } from '#~/device/index.js';
import { DeviceRegistryCommandKey } from '@tryiris-ai/common.protojs/api/v1/device_settings';

export const confirmSettings = (
  prompt: UsePrompt,
  requiresReboot: boolean,
  onConfirm: (result: boolean) => void
): void => {
  prompt.confirm({
    title: 'Apply Stable Profile',
    className: 'pb-24',
    cancel: {
      label: 'Cancel'
    },
    okay: {
      label: requiresReboot ? 'Apply profile and reboot' : 'Apply Profile'
    },
    message:
      'Applying the stable profile will lower video resolution and bitrate to reduce CPU/RAM usage and improve device stability.',
    onConfirm: (answer: boolean) => {
      onConfirm(answer);
    }
  });
};

export const askApplySettings = (prompt: UsePrompt, deviceId: Device['id']): void => {
  const proxy = locateProxy(deviceId);
  if (!proxy) {
    return;
  }

  // Check if the device has settings to be applied.
  const suggestedSettings = proxy.state.status?.device[deviceId]?.deviceSettings?.suggestedSettings;
  if (!suggestedSettings || Object.keys(suggestedSettings).length === 0) {
    return;
  }

  // Get command definitions.
  const commands = proxy.state.status?.device[deviceId]?.deviceSettings?.commandDefinitions;
  if (!commands || Object.keys(commands).length === 0) {
    return;
  }

  // Get interface sections.
  const interfaceSections = proxy.state.status?.device[deviceId]?.deviceSettings?.interfaceSections;
  if (!interfaceSections) {
    return;
  }

  const requiresReboot = Object.values(suggestedSettings).some(
    (setting) =>
      setting.key === DeviceRegistryCommandKey.POWER ||
      setting.key === DeviceRegistryCommandKey.SYSTEM_REBOOT
  );

  // Callback for applying settings after user confirmation.
  const onConfirmSettings = (apply: boolean) => {
    if (apply) {
      // hideNotification.
      clear(`high-load-warning-${deviceId}`);

      // Apply suggested settings.
      applySettings(deviceId);
    }
  };

  // Show confirmation dialog.
  confirmSettings(prompt, requiresReboot, onConfirmSettings);
};

export const applySettings = (deviceId: Device['id']) => {
  const proxy = locateProxy(deviceId);

  if (!proxy) {
    return;
  }

  proxy.mutate(
    {
      command: {
        device: {
          image: {
            applySuggestedSettings: {
              id: deviceId
            }
          }
        }
      }
    },
    {}
  );
};

export const enableVideo = (deviceId: Device['id']) => {
  const proxy = locateProxy(deviceId);

  if (!proxy) {
    return;
  }

  proxy.mutate(
    {
      command: {
        device: {
          stream: {
            enable: {
              id: deviceId,
              enable: true
            }
          }
        }
      }
    },
    {}
  );
};
